// src/pages/onboarding/doctor/steps/StepDoneDoctor.jsx
import React from "react";
import OnboardingTopBar from "@/components/onboarding/OnboardingTopBar";
import OnboardingSection from "@/components/onboarding/OnboardingSection";
import Button from "@/components/ui/Button";
import supabase from "@/services/supabaseClient";
import { useAuth } from "@/contexts/AuthContext";
import { canUseOnboardingDraftMode } from "@/lib/onboardingDraftMode";

export default function StepDoneDoctor({
  step,
  total,
  onBack,
  onSkip,
  fallbackPhone = "",
  heroIcon = "/icons/doctor1.svg",
  contextNote = "",
  onTelemetry,
}) {
  const { refreshProfile } = useAuth();
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState("");

  async function syncDoctorProfileFromAuth() {
    const { data: { user }, error: authErr } = await supabase.auth.getUser();
    if ((authErr || !user) && canUseOnboardingDraftMode()) return;
    if (authErr || !user) throw new Error("Session expired. Please try again.");

    const { data: existingProfile } = await supabase
      .from("doctor_profiles")
      .select("email,email_verified_at,phone,phone_verified_at,onboarding_completed,onboarding_completed_at")
      .eq("user_id", user.id)
      .maybeSingle();

    const authPhone = String(user.phone || "").trim();
    const seededPhone = String(fallbackPhone || "").trim();
    const nextPhone = authPhone || seededPhone || existingProfile?.phone || null;

    const payload = {
      user_id: user.id,
      email: user.email ?? existingProfile?.email ?? null,
      email_verified_at:
        user.email_confirmed_at || existingProfile?.email_verified_at || null,
      phone: nextPhone,
      phone_verified_at:
        user.phone_confirmed_at || existingProfile?.phone_verified_at || null,
      onboarding_completed: true,
      onboarding_completed_at:
        existingProfile?.onboarding_completed_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const { error: upsertErr } = await supabase
      .from("doctor_profiles")
      .upsert(payload, { onConflict: "user_id" });

    if (upsertErr) throw upsertErr;

    const { error: profileErr } = await supabase
      .from("profiles")
      .upsert(
        {
          user_id: user.id,
          role: "doctor",
          role_selected: true,
          onboarding_completed: true,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id" }
      );

    if (profileErr) throw profileErr;

    if (typeof refreshProfile === "function") {
      await refreshProfile();
    }
  }

  async function runAndContinue(next) {
    if (saving) return;
    setErr("");
    setSaving(true);
    onTelemetry?.("continue_clicked", { valid: true, saving: false });

    try {
      onTelemetry?.("save_started");
      await Promise.race([
        syncDoctorProfileFromAuth(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Connection timeout")), 8000)
        ),
      ]);
      onTelemetry?.("save_succeeded");
      await Promise.resolve(next?.());
    } catch (e) {
      console.error("[StepDoneDoctor] Sync failed:", e?.message);
      onTelemetry?.("save_failed", { reason: "step_done_sync_failed" });
      setErr(e?.message || "Could not sync profile. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-dvh flex flex-col">
      <OnboardingTopBar step={step} total={total} onBack={onBack} />

      <OnboardingSection
        header={null}
        center
        bodyClassName="flex-1 px-4 !min-h-[calc(100dvh-var(--onb-footer-h)-120px)] md:!min-h-[calc(100dvh-var(--onb-footer-h)-170px)]"
        footer={
          <div className="w-full max-w-[520px] mx-auto px-4 pointer-events-auto">
            <Button
              fullWidth
              size="lg"
              loading={saving}
              disabled={saving}
              onClick={() => runAndContinue(onSkip)}
            >
              <span className="text-title-medium">Go to settings</span>
            </Button>

            {!!err && (
              <p className="mt-2 text-body-small text-[rgb(var(--afrimed-danger))] text-center">
                {err}
              </p>
            )}
          </div>
        }
      >
        <div className="w-full flex flex-col items-center justify-center text-center">
          <div className="w-[200px] h-[200px] flex items-center justify-center">
            <img
              src="/icons/profile-complete1.svg"
              alt=""
              className="w-[126px] h-[127px] object-contain select-none"
              draggable="false"
            />
          </div>

          <h2 className="mt-8 text-center text-headline-small text-on-surface">
            You’re all set!
          </h2>
          {contextNote ? (
            <p className="mb-5 max-w-[520px] text-center text-body-small text-on-surface-var">
              {contextNote}
            </p>
          ) : null}

        </div>
      </OnboardingSection>
    </div>
  );
}
